import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { UseMutationResult } from '@tanstack/react-query';
import { GridApi } from 'ag-grid-community';
import { Box, Typography } from '@mui/material';
import { getPositionValue } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/gridHelpers';
import MappingValueSelector from './MappingValueSelector';
import { HrMappingFieldEnum, HrPositionMapping } from '../helpers/type';
import { PutPositionMappingPayload } from '../helpers/hooks/hrPositionMapping';
import { StatusSelectorOptions } from 'components/SharedComponents/StatusSelector/StatusSelector';

type Props = {
    selectedMappingsIds: number[];
    updateMappingsMutation: UseMutationResult;
    gridApi: GridApi;
    fieldToUpdate: keyof PutPositionMappingPayload;
    fieldEnum: HrMappingFieldEnum;
};

export default function MappingFieldHeaderSelector({
    selectedMappingsIds,
    updateMappingsMutation,
    gridApi,
    fieldToUpdate,
    fieldEnum,
}: Props) {
    const { t } = useTranslation();
    const mappingValuesChoices = Object.values(fieldEnum).map((value) => ({
        value,
        label: getPositionValue(value),
    }));
    const handleChange = useCallback(
        (e: StatusSelectorOptions<HrPositionMapping>) => {
            const newValue = e.value;
            updateMappingsMutation.mutate({
                [fieldToUpdate]: newValue,
                hrPositionMappingIds: selectedMappingsIds,
            });
            gridApi.deselectAll();
        },
        [selectedMappingsIds, updateMappingsMutation],
    );

    return (
        <Box sx={{ flexDirection: 'row' }}>
            <Typography>
                {t(`humanResources.settings.${fieldToUpdate}`)}
            </Typography>
            <MappingValueSelector
                handleChange={handleChange}
                choices={mappingValuesChoices}
            />
        </Box>
    );
}
